import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsResultsService {
  private baseApi: string = environment.apiUrl + "/analytics-results/";

  constructor(private http: HttpClient) { }

  public getBankAccounts(): Observable<AccountResult[]> {
    return this.http.get<AccountResult[]>(this.baseApi + "accounts");
  }

  public getCategoryGroupsForTransactionsCurrentYear(): Observable<TransactionCategoryGroupStatistic[]> {
    return this.http.get<TransactionCategoryGroupStatistic[]>(this.baseApi + "transactions/category-groups");
  }

  public getCategoryGroupsForTransactionsLast12Months(): Observable<TransactionCategoryGroupStatistic[]> {
    return this.http.get<TransactionCategoryGroupStatistic[]>(this.baseApi + "transactions/category-groups/12-months");
  }

  public getCategoriesForTransactionsCurrentYear(): Observable<TransactionCategoryStatistic[]> {
    return this.http.get<TransactionCategoryStatistic[]>(this.baseApi + "transactions/categories/");
  }

  public getCategoriesForTransactionsLast12Months(): Observable<TransactionCategoryStatistic[]> {
    return this.http.get<TransactionCategoryStatistic[]>(this.baseApi + "transactions/categories/12-months");
  }

  public getDailyBalanceByAccountYearToDate(accountId: string): Observable<DailyBalanceStatistic[]> {
    return this.http.get<DailyBalanceStatistic[]>(this.baseApi + "accounts/" + accountId + "/daily-balance/ytd");
  }

  public getDailyBalanceByAccountByYear(accountId: string, yearNumber: number): Observable<DailyBalanceStatistic[]> {
    return this.http.get<DailyBalanceStatistic[]>(this.baseApi + "accounts/" + accountId + "/daily-balance/by-year/" + yearNumber);
  }

  public getDailyBalanceByAccountLastYear(accountId: string): Observable<DailyBalanceStatistic[]> {
    return this.http.get<DailyBalanceStatistic[]>(this.baseApi + "accounts/" + accountId + "/daily-balance/last-year");
  }

  public getDailyBalanceYearToDate(): Observable<DailyBalanceStatisticAll[]> {
    return this.http.get<DailyBalanceStatisticAll[]>(this.baseApi + "accounts/daily-balance/ytd");
  }

  public getDailyBalanceByYear(yearNumber: number): Observable<DailyBalanceStatisticAll[]> {
    return this.http.get<DailyBalanceStatisticAll[]>(this.baseApi + "accounts/daily-balance/by-year/" + yearNumber);
  }

  public getDailyBalanceLastYear(): Observable<DailyBalanceStatisticAll[]> {
    return this.http.get<DailyBalanceStatisticAll[]>(this.baseApi + "accounts/daily-balance/last-year");
  }

  public getIncomeVsSpendStatistics(): Observable<IncomeVsSpendStatistic[]> {
    return this.http.get<IncomeVsSpendStatistic[]>(this.baseApi + "accounts/income-vs-spend");
  }

  public getSpendByDayStatistics(): Observable<DailySpendStatistic[]> {
    return this.http.get<DailySpendStatistic[]>(this.baseApi + "accounts/spend-by-day");
  }

  public getSpendByDayByAccountStatistics(accountId: string): Observable<DailySpendStatistic[]> {
    return this.http.get<DailySpendStatistic[]>(this.baseApi + "accounts/" + accountId + "/spend-by-day");
  }

}

export class AccountResult {
  constructor() {
    this.BalanceRefreshedAt = new Date();
    this.CreatedAt = new Date();
    this.UpdatedAt = new Date();
    this.LastSyncedAt = new Date();
  }

  public BankAccountId: string = "";
  public Name: string = "";
  public Timezone: string = "";
  public BankName: string = "";
  public BankLogoUrl: string = "";
  public Status: string = "";
  public FormattedAccount: string = "";
  public AccountType: string = "";
  public CurrentBalance: number = 0;
  public AvailableBalance: number | undefined;
  public BalanceRefreshedAt: Date;
  public TransactionsRefreshedAt: Date | undefined;
  public CreditLimit: number | undefined;
  public IsOverdrawn: boolean | undefined;
  public Currency: string = "";
  public CreatedAt: Date;
  public UpdatedAt: Date;
  public LastSyncedAt: Date;
}


export class TransactionCategoryGroupStatistic {
  public dim_txncatgrp: string = "";
  public dim_txndate_yearmonthname: string = "";
  public dim_txndate_yearmonth: number | undefined;
  public fct_count_abs_amount: number | undefined;
  public fct_min_abs_amount: number | undefined;
  public fct_max_abs_amount: number | undefined;
  public fct_sum_abs_amount: number | undefined;
  public fct_p25_abs_amount: number | undefined;
  public fct_p50_abs_amount: number | undefined;
  public fct_p75_abs_amount: number | undefined;
  public fct_avg_abs_amount: number | undefined;
  public fct_count_amount: number | undefined;
  public fct_min_amount: number | undefined;
  public fct_max_amount: number | undefined;
  public fct_sum_amount: number | undefined;
  public fct_p25_amount: number | undefined;
  public fct_p50_amount: number | undefined;
  public fct_p75_amount: number | undefined;
  public fct_avg_amount: number | undefined;
}

export class TransactionCategoryStatistic {
  public dim_txncat: string = "";
  public dim_txndate_yearmonthname: string = "";
  public dim_txndate_yearmonth: number | undefined;
  public fct_count_abs_amount: number | undefined;
  public fct_min_abs_amount: number | undefined;
  public fct_max_abs_amount: number | undefined;
  public fct_sum_abs_amount: number | undefined;
  public fct_p25_abs_amount: number | undefined;
  public fct_p50_abs_amount: number | undefined;
  public fct_p75_abs_amount: number | undefined;
  public fct_avg_abs_amount: number | undefined;
  public fct_count_amount: number | undefined;
  public fct_min_amount: number | undefined;
  public fct_max_amount: number | undefined;
  public fct_sum_amount: number | undefined;
  public fct_p25_amount: number | undefined;
  public fct_p50_amount: number | undefined;
  public fct_p75_amount: number | undefined;
  public fct_avg_amount: number | undefined;
}

export class DailyBalanceStatistic {
  constructor() {
    this.dim_balancedate_date = new Date();
  }

  public dim_balancedate_date: Date;
  public fct_currentbalance: number | undefined;
  public fct_availablebalance: number | undefined;
  public fct_creditlimit: number | undefined;
  public fct_isoverdrawn: boolean = false;
}

export class DailyBalanceStatisticAll {
  constructor() {
    this.dim_balancedate_date = new Date();
  }

  public dim_balancedate_date: Date;
  public fct_sum_currentbalance: number | undefined;
  public fct_sum_availablebalance: number | undefined;
}

export class IncomeVsSpendStatistic {
  constructor() {
    this.dim_txndate_yearmonth_date = new Date();
  }
  
  dim_txndate_year: number | undefined;
  dim_txndate_quarter: number | undefined;
  dim_txndate_month: number | undefined;
  dim_txndate_quartername: string | undefined;
  dim_txndate_yearquartername: string | undefined;
  dim_txndate_yearmonth: number | undefined;
  dim_txndate_yearmonth_date: Date;
  dim_txndate_monthname: string | undefined;
  dim_txndate_yearmonthname: string | undefined;
  fct_income: number | undefined;
  fct_spend: number | undefined;
}

export class DailySpendStatistic {
  constructor() {
    this.dim_txndate_day_date = new Date();
  }

  dim_txndate_day_date: Date;
  fct_spend: number | undefined;
  fct_abs_spend: number | undefined;
}